'use client';

import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Loader } from '@mantine/core';
import { Dropzone as MantineDropzone, FileWithPath } from '@mantine/dropzone';
import { useDictionary } from '@/src/dictionaries/dictionary-provider';
import styles from './Dropzone.module.scss';

export const Dropzone = ({
  setImageUrl,
  imageUrl,
  error,
  required
}: {
  setImageUrl: Dispatch<SetStateAction<string | null>>;
  imageUrl: string | null;
  error?: string;
  required?: boolean;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const dictionary = useDictionary();

  // 👇 Sync preview with external value
  const [preview, setPreview] = useState<string | null>(imageUrl);
  useEffect(() => {
    setPreview(imageUrl);
  }, [imageUrl]);

  const handleDrop = async (acceptedFiles: FileWithPath[]) => {
    setIsLoading(true);
    const file = acceptedFiles[0];

    try {
      const compressedFile = await compressImage(file, 800, 75);
      const formData = new FormData();
      formData.append('file', compressedFile);

      const response = await fetch('/api/upload', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const { url } = await response.json();
        setImageUrl(url);
        setPreview(url); // 👈 Force preview update
      } else {
        alert("Le fichier n'est pas sauvegardé. Veuillez réessayer");
      }
    } catch (error) {
      console.error('❌ Error uploading image:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <MantineDropzone
        onDrop={handleDrop}
        accept={['image/*']}
        multiple={false}
        className={styles.dropzone}
        style={{
          border: `2px dashed ${error ? 'red' : 'black'}`,
          marginBottom: `${error ? '0' : '20px'}`,
        }}
      >
        <div style={{ textAlign: 'center' }}>
          {!preview && isLoading ? (
            <Loader />
          ) : !preview ? (
            <>
              <p>{dictionary?.general?.dropzone}</p>
              {!required && <p style={{ fontSize: '0.8rem' }}>{dictionary?.general?.optional}</p>}
            </>
          ) : (
            <img
              src={preview}
              alt="Uploaded Image"
              style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'cover' }}
            />
          )}
        </div>
      </MantineDropzone>
      {error && <p style={{ color: 'red', fontSize: '0.8rem' }}>{error}</p>}
    </>
  );
};

async function compressImage(file: File, maxWidth: number, quality: number): Promise<File> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        const scaleFactor = maxWidth / img.width;
        canvas.width = maxWidth;
        canvas.height = img.height * scaleFactor;

        ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(
          (blob) => {
            if (!blob) return reject(new Error('Compression failed'));
            const compressedFile = new File([blob], file.name, { type: 'image/jpeg' });
            resolve(compressedFile);
          },
          'image/jpeg',
          quality / 100
        );
      };
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}
